import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

const PlatformUpdates = () => {
  const [activeTab, setActiveTab] = useState('recent');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const recentUpdates = [
    {
      id: 1,
      type: 'recipes',
      title: 'New Eczema-Friendly Breakfast Collection',
      description: '12 new anti-inflammatory breakfast recipes added, all dairy-free and gluten-free',
      date: '2 days ago',
      icon: '🥞',
      color: 'from-green-50 to-emerald-50 border-green-200',
      items: ['Coconut Chia Pudding', 'Anti-inflammatory Smoothie Bowl', 'Healing Turmeric Pancakes']
    },
    {
      id: 2,
      type: 'workouts',
      title: 'Gentle Movement Program Added',
      description: 'New 4-week program designed specifically for those with chronic conditions',
      date: '1 week ago',
      icon: '🧘‍♀️',
      color: 'from-blue-50 to-cyan-50 border-blue-200',
      items: ['Chair Yoga Sequences', 'Gentle Stretching', 'Breathing Exercises']
    },
    {
      id: 3,
      type: 'community',
      title: 'New Forum Categories',
      description: 'Added specialized discussion groups for different health conditions',
      date: '2 weeks ago',
      icon: '💬',
      color: 'from-purple-50 to-pink-50 border-purple-200',
      items: ['Autoimmune Support', 'Skin Health', 'Mental Wellness']
    },
    {
      id: 4,
      type: 'resources',
      title: 'Exercise Tutorial Library',
      description: 'Comprehensive form guides and video tutorials for all workout movements',
      date: '3 weeks ago',
      icon: '📚',
      color: 'from-orange-50 to-yellow-50 border-orange-200',
      items: ['Hip Thrust Guide', 'Shoulder Press Tutorial', 'Tricep Dips Form']
    }
  ];

  const upcomingFeatures = [
    {
      id: 1,
      title: 'Live Q&A Sessions with Kate',
      description: 'Monthly live sessions to answer your wellness questions directly',
      timeline: 'Coming in February 2024',
      icon: '🎥',
      status: 'in-development'
    },
    {
      id: 2,
      title: 'Personalized Meal Planning',
      description: 'AI-powered meal plans based on your specific allergies and preferences',
      timeline: 'Coming in March 2024',
      icon: '🍽️',
      status: 'planned'
    },
    {
      id: 3,
      title: 'Symptom Tracking Integration',
      description: 'Track your symptoms and see correlations with diet and exercise',
      timeline: 'Coming in April 2024',
      icon: '📊',
      status: 'planned'
    },
    {
      id: 4,
      title: 'Mobile App Launch',
      description: 'Native iOS and Android apps for seamless on-the-go access',
      timeline: 'Coming in Summer 2024',
      icon: '📱',
      status: 'research'
    }
  ];

  const communityStats = {
    totalMembers: 1247,
    newThisMonth: 89,
    activeDiscussions: 156,
    recipesShared: 342,
    workoutsCompleted: 2891
  };

  const tabs = [
    { id: 'recent', name: 'Recent Updates', icon: '🆕' },
    { id: 'upcoming', name: 'Coming Soon', icon: '🚀' },
    { id: 'community', name: 'Community Growth', icon: '📈' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-beige-50 to-sage-100 pt-20 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <RouterLink
              to="/dashboard"
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </RouterLink>
            <h1 className="text-3xl font-playfair font-semibold text-charcoal-900">Platform Updates</h1>
          </div>
          <div className="text-sm text-charcoal-600">
            See what's new and what's coming
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-8 bg-white/60 backdrop-blur-sm rounded-2xl p-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-xl font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-sage-600 text-white shadow-soft'
                  : 'text-charcoal-600 hover:bg-white/50'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Recent Updates Tab */}
        {activeTab === 'recent' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
            >
              <h2 className="text-xl font-semibold text-charcoal-900 mb-6">Latest Platform Updates</h2>
              <div className="space-y-4">
                {recentUpdates.map((update, index) => (
                  <motion.div
                    key={update.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`bg-gradient-to-r ${update.color} rounded-2xl p-4 border`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="text-3xl">{update.icon}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-charcoal-800">{update.title}</h3>
                          <span className="text-sm text-charcoal-500">{update.date}</span>
                        </div>
                        <p className="text-sm text-charcoal-600 mb-3">{update.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {update.items.map((item, itemIndex) => (
                            <span key={itemIndex} className="bg-white/70 text-charcoal-700 px-2 py-1 rounded-full text-xs">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        )}

        {/* Upcoming Features Tab */}
        {activeTab === 'upcoming' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
            >
              <h2 className="text-xl font-semibold text-charcoal-900 mb-6">Coming Soon</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {upcomingFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-4 border border-blue-200"
                  >
                    <div className="flex items-start space-x-3 mb-3">
                      <span className="text-2xl">{feature.icon}</span>
                      <div className="flex-1">
                        <h3 className="font-semibold text-charcoal-800 mb-1">{feature.title}</h3>
                        <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          feature.status === 'in-development' ? 'bg-orange-100 text-orange-700' :
                          feature.status === 'planned' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {feature.status === 'in-development' ? 'In Development' :
                           feature.status === 'planned' ? 'Planned' : 'Research Phase'}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-charcoal-600 mb-3">{feature.description}</p>
                    <div className="text-xs text-charcoal-500 font-medium">{feature.timeline}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Live Q&A Announcement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-3xl p-6 border border-purple-200"
            >
              <div className="text-center">
                <div className="text-4xl mb-3">🎥</div>
                <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Live Q&A with Kate Martin</h3>
                <p className="text-charcoal-600 mb-4">
                  Join our founder for monthly live sessions where she answers your wellness questions directly. 
                  Get personalized advice and connect with the community in real-time.
                </p>
                <div className="bg-white/70 rounded-xl p-3 mb-4">
                  <div className="text-sm font-medium text-purple-800 mb-1">Next Session</div>
                  <div className="text-lg font-semibold text-purple-900">February 15th, 2024 at 7 PM EST</div>
                </div>
                <button className="bg-purple-600 text-white px-6 py-2 rounded-xl font-medium hover:bg-purple-700 transition-colors">
                  Get Notified
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Community Growth Tab */}
        {activeTab === 'community' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
            >
              <h2 className="text-xl font-semibold text-charcoal-900 mb-6">Community Growth</h2>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-1">{communityStats.totalMembers.toLocaleString()}</div>
                  <div className="text-sm text-charcoal-600">Total Members</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-600 mb-1">+{communityStats.newThisMonth}</div>
                  <div className="text-sm text-charcoal-600">New This Month</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600 mb-1">{communityStats.activeDiscussions}</div>
                  <div className="text-sm text-charcoal-600">Active Discussions</div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600 mb-1">{communityStats.recipesShared}</div>
                  <div className="text-sm text-charcoal-600">Recipes Shared</div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-4 text-center">
                  <div className="text-2xl font-bold text-red-600 mb-1">{communityStats.workoutsCompleted.toLocaleString()}</div>
                  <div className="text-sm text-charcoal-600">Workouts Completed</div>
                </div>
              </div>

              {/* Growth Message */}
              <div className="bg-gradient-to-r from-sage-50 to-beige-50 rounded-2xl p-6 border border-sage-200">
                <div className="text-center">
                  <div className="text-3xl mb-3">🌱</div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Our Community is Thriving!</h3>
                  <p className="text-charcoal-600 leading-relaxed">
                    Every month, we're adding new resources, recipes, and workouts based on your feedback and needs. 
                    Your participation and support help us create the most comprehensive wellness platform for people 
                    with allergies and chronic conditions. Thank you for being part of this healing journey! ✨
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformUpdates;
